/**
 * Admin Invoice Ledger — /admin/invoices
 *
 * A single-page ledger that shows every successful payment across all
 * gateways (Paytm, SabPaisa, Instamojo, Stripe) with filters, summary cards, and
 * three export options (CSV, ZIP of invoice PDFs, and a monthly
 * summary for GSTR-1-style filing later).
 *
 * Admin/moderator only. Reads:
 *   GET /api/subscriptions/admin/invoices
 *   GET /api/subscriptions/admin/invoices/export.csv
 *   GET /api/subscriptions/admin/invoices/export.zip
 *   GET /api/subscriptions/admin/invoices/statement.pdf
 *   GET /api/subscriptions/admin/invoices/monthly-summary
 *
 * See handoff PRD §Admin Invoice Ledger for the full spec.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Download, FileArchive, FileSpreadsheet, FileText, Filter,
  IndianRupee, Loader2, RefreshCw, Users, Calendar,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Indian FY label for the current date. */
function currentFyLabel() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? y : y - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
}

/** Enumerate the last N FYs including the current one (newest first). */
function recentFyLabels(n = 5) {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? y : y - 1;
  return Array.from({ length: n }, (_, i) => {
    const s = startYear - i;
    return `${s}-${String(s + 1).slice(-2)}`;
  });
}

/** Formatting helpers */
const fmtINR = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
};

export default function AdminInvoicesPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Filter state
  const [fy, setFy] = useState(currentFyLabel());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [gateway, setGateway] = useState('all');
  const [orderId, setOrderId] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [useCustomDates, setUseCustomDates] = useState(false);

  // Data state
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingSummary, setExportingSummary] = useState(false);

  // Guard: admin only
  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'moderator') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (useCustomDates) {
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('date_to', new Date(dateTo).toISOString());
    } else if (fy) {
      params.set('fy', fy);
    }
    if (gateway && gateway !== 'all') params.set('gateways', gateway);
    if (orderId.trim()) params.set('order_id', orderId.trim());
    if (minAmount) params.set('min_amount', minAmount);
    if (maxAmount) params.set('max_amount', maxAmount);
    return params.toString();
  }, [useCustomDates, dateFrom, dateTo, fy, gateway, orderId, minAmount, maxAmount]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = buildQuery();
      const res = await axios.get(
        `${API}/api/subscriptions/admin/invoices?${qs}`,
        { headers: authHeader, withCredentials: true },
      );
      setRows(res.data.rows || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load invoice ledger.');
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [authHeader, buildQuery]);

  useEffect(() => { load(); }, [load]);

  // Export helpers — trigger a browser file download.
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const qs = buildQuery();
      const res = await axios.get(
        `${API}/api/subscriptions/admin/invoices/export.csv?${qs}`,
        { headers: authHeader, withCredentials: true, responseType: 'blob' },
      );
      downloadBlob(res.data, `misyou-invoices-${Date.now()}.csv`);
    } catch (err) {
      setError('CSV export failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportingCsv(false);
    }
  };

  const exportZip = async () => {
    setExportingZip(true);
    try {
      const qs = buildQuery();
      const res = await axios.get(
        `${API}/api/subscriptions/admin/invoices/export.zip?${qs}`,
        { headers: authHeader, withCredentials: true, responseType: 'blob' },
      );
      downloadBlob(res.data, `misyou-invoices-${Date.now()}.zip`);
    } catch (err) {
      setError('ZIP export failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportingZip(false);
    }
  };

  const exportStatementPdf = async () => {
    setExportingPdf(true);
    try {
      const qs = buildQuery();
      const res = await axios.get(
        `${API}/api/subscriptions/admin/invoices/statement.pdf?${qs}`,
        { headers: authHeader, withCredentials: true, responseType: 'blob' },
      );
      downloadBlob(res.data, `misyou-statement-${Date.now()}.pdf`);
    } catch (err) {
      setError('Statement PDF failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportingPdf(false);
    }
  };

  const downloadOne = async (row) => {
    try {
      const res = await axios.get(
        `${API}/api/subscriptions/admin/${encodeURIComponent(row.txn_ref)}/invoice`,
        { headers: authHeader, withCredentials: true, responseType: 'blob' },
      );
      const invoiceId = row.invoice_id || `invoice-${row.txn_ref?.slice(0, 12)}`;
      downloadBlob(res.data, `${invoiceId.replace(/[\\/]/g, '_')}.pdf`);
    } catch (err) {
      setError('Invoice download failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const downloadMonthlySummary = async () => {
    setExportingSummary(true);
    try {
      const qs = buildQuery();
      const res = await axios.get(
        `${API}/api/subscriptions/admin/invoices/monthly-summary?${qs}`,
        { headers: authHeader, withCredentials: true },
      );
      const csvLines = [
        'Month,Transactions,Gross Revenue (INR),CGST,SGST,IGST',
        ...(res.data.months || []).map(m =>
          `"${m.month_label}",${m.transactions},${m.gross_revenue_inr.toFixed(2)},${m.cgst_inr.toFixed(2)},${m.sgst_inr.toFixed(2)},${m.igst_inr.toFixed(2)}`,
        ),
        '',
        `TOTAL,${res.data.total_transactions},${res.data.total_gross_revenue_inr.toFixed(2)},0.00,0.00,0.00`,
      ];
      downloadBlob(
        new Blob(['\ufeff' + csvLines.join('\n')], { type: 'text/csv' }),
        `misyou-monthly-summary-${Date.now()}.csv`,
      );
    } catch (err) {
      setError('Monthly summary failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportingSummary(false);
    }
  };

  const clearFilters = () => {
    setFy(currentFyLabel());
    setUseCustomDates(false);
    setDateFrom(''); setDateTo('');
    setGateway('all');
    setOrderId('');
    setMinAmount(''); setMaxAmount('');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-invoices-page">
      {/* Header bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">Invoice Ledger</h1>
            <p className="text-xs text-slate-500">
              All successful payments across Paytm, SabPaisa, Instamojo and Stripe · GST status: <strong>Exempt (Below Threshold)</strong>
            </p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={load}
            disabled={loading}
            data-testid="refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* FY vs Custom date toggle */}
              <div>
                <Label className="text-xs">Date range</Label>
                <Select
                  value={useCustomDates ? 'custom' : fy}
                  onValueChange={(v) => {
                    if (v === 'custom') { setUseCustomDates(true); }
                    else { setUseCustomDates(false); setFy(v); }
                  }}
                >
                  <SelectTrigger data-testid="filter-fy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {recentFyLabels(6).map(f => (
                      <SelectItem key={f} value={f}>FY {f}</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom range…</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {useCustomDates && (
                <>
                  <div>
                    <Label className="text-xs">From date</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      data-testid="filter-date-from"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">To date</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      data-testid="filter-date-to"
                    />
                  </div>
                </>
              )}

              <div>
                <Label className="text-xs">Gateway</Label>
                <Select value={gateway} onValueChange={setGateway}>
                  <SelectTrigger data-testid="filter-gateway-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All gateways</SelectItem>
                    <SelectItem value="paytm">Paytm</SelectItem>
                    <SelectItem value="sabpaisa">SabPaisa</SelectItem>
                    <SelectItem value="instamojo">Instamojo</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className={useCustomDates ? '' : 'md:col-span-2'}>
                <Label className="text-xs">Order ID contains</Label>
                <Input
                  placeholder="e.g. MISYOU_ABC..."
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  data-testid="filter-order-id"
                />
              </div>

              <div>
                <Label className="text-xs">Min amount (INR)</Label>
                <Input
                  type="number" min="0"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  data-testid="filter-min-amount"
                />
              </div>

              <div>
                <Label className="text-xs">Max amount (INR)</Label>
                <Input
                  type="number" min="0"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  data-testid="filter-max-amount"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                onClick={load}
                disabled={loading}
                data-testid="apply-filters-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Filter className="w-4 h-4 mr-1" />}
                Apply filters
              </Button>
              <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                Clear
              </Button>
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={exportingCsv || loading}
                data-testid="export-csv-btn"
              >
                {exportingCsv ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                Download CSV
              </Button>
              <Button
                variant="outline"
                onClick={exportZip}
                disabled={exportingZip || loading}
                data-testid="export-zip-btn"
              >
                {exportingZip ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileArchive className="w-4 h-4 mr-1" />}
                All PDFs (ZIP)
              </Button>
              <Button
                variant="outline"
                onClick={exportStatementPdf}
                disabled={exportingPdf || loading}
                data-testid="export-statement-pdf-btn"
              >
                {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
                Statement PDF
              </Button>
              <Button
                variant="outline"
                onClick={downloadMonthlySummary}
                disabled={exportingSummary || loading}
                data-testid="export-monthly-btn"
              >
                {exportingSummary ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Calendar className="w-4 h-4 mr-1" />}
                Monthly Summary
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm"
            data-testid="ledger-error"
          >
            {error}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard
            testid="summary-gross"
            label="Gross revenue"
            value={fmtINR(summary?.gross_revenue_inr)}
            icon={<IndianRupee className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-transactions"
            label="Transactions"
            value={summary?.transactions_count ?? 0}
            icon={<FileSpreadsheet className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-customers"
            label="Unique customers"
            value={summary?.unique_customers ?? 0}
            icon={<Users className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-tax"
            label="Tax collected"
            value={fmtINR(0)}
            hint="0% — GST-exempt"
            icon={<IndianRupee className="w-4 h-4" />}
          />
        </div>

        {/* Ledger table */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              {rows.length} invoice{rows.length !== 1 ? 's' : ''}
            </CardTitle>
            <p className="text-xs text-slate-500">
              Sorted by date, newest first
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 text-left">Invoice No</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Order ID / Txn</th>
                    <th className="px-4 py-2 text-left">Gateway</th>
                    <th className="px-4 py-2 text-left">Customer</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2 text-center">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-slate-500">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-slate-400" data-testid="ledger-empty">
                        No invoices match these filters. Adjust the date range or gateway above.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.txn_ref} data-testid={`ledger-row-${r.txn_ref}`}>
                        <td className="px-4 py-2 font-mono text-xs">
                          {r.invoice_id || <span className="text-slate-400">— not yet stamped —</span>}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-700">
                          {fmtDate(r.created_at)}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600">
                          {r.txn_ref}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {r.gateway}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <div className="font-medium text-slate-900">{r.customer_name || '—'}</div>
                          <div className="text-slate-500">{r.customer_email || ''}</div>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold">
                          {fmtINR(r.amount_inr)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => downloadOne(r)}
                            data-testid={`download-invoice-${r.txn_ref}`}
                            title="Download invoice PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, hint, icon, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</span>
          <span className="text-slate-400">{icon}</span>
        </div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}
